const hasContent = (text) => typeof text === 'string' && text.trim().length > 0
const TOOLTIP_POPUP_CLASS = 'v-tooltip-popup'
const TOOLTIP_PORTAL_CLASS = 'v-tooltip-popup--portal'
const TOOLTIP_PORTAL_VISIBLE_CLASS = 'v-tooltip-popup--visible'

const getTooltipPopup = (el) =>
  Array.from(el.children).find((child) => child.classList?.contains(TOOLTIP_POPUP_CLASS)) ?? null

const ensureTooltipPopup = (el) => {
  const existingPopup = getTooltipPopup(el)
  if (existingPopup) return existingPopup

  const popup = document.createElement('span')
  popup.className = TOOLTIP_POPUP_CLASS
  popup.setAttribute('aria-hidden', 'true')
  el.appendChild(popup)
  return popup
}

const removeTooltipPopup = (el) => {
  getTooltipPopup(el)?.remove()
}

const removePortalTooltipPopup = (el) => {
  el._vTooltipPortalPopup?.remove()
  el._vTooltipPortalPopup = null
}

// Renders the text, optionally below a bold title ({ title, text }).
const renderTooltipContent = (popup, text, title) => {
  popup.textContent = ''
  popup.classList.toggle('v-tooltip-popup--titled', hasContent(title))
  if (!hasContent(title)) {
    popup.appendChild(document.createTextNode(text))
    return
  }

  const titleEl = document.createElement('strong')
  titleEl.className = 'v-tooltip-title'
  titleEl.textContent = title
  const bodyEl = document.createElement('span')
  bodyEl.className = 'v-tooltip-body'
  bodyEl.textContent = text
  popup.append(titleEl, bodyEl)
}

const syncTooltipPopup = (el, text, title) => {
  if (!hasContent(text)) {
    removeTooltipPopup(el)
    return
  }

  renderTooltipContent(ensureTooltipPopup(el), text, title)
}

const applyPortalTheme = (popup, theme = {}) => {
  for (const [key, val] of Object.entries(theme)) {
    if (key === 'placement') {
      switch (val) {
        case 'top':
          popup.style.setProperty(
            '--v-tooltip-translate',
            'translate(-50%, calc(-100% - var(--v-tooltip-arrow-offset)))'
          )
          popup.style.setProperty('--v-tooltip-arrow-left', 'calc(50% + var(--v-tooltip-left-offset))')
          popup.style.setProperty('--v-tooltip-arrow-top', '100%')
          popup.style.setProperty(
            '--v-tooltip-arrow-translate',
            'translate(-50%, calc(-1 * var(--v-tooltip-arrow-overlap)))'
          )
          popup.style.setProperty(
            '--v-tooltip-arrow-border-color',
            'var(--v-tooltip-background-color) transparent transparent transparent'
          )
          break
        case 'bottom':
          popup.style.setProperty('--v-tooltip-translate', 'translate(-50%, var(--v-tooltip-arrow-offset))')
          popup.style.setProperty('--v-tooltip-arrow-left', 'calc(50% + var(--v-tooltip-left-offset))')
          popup.style.setProperty('--v-tooltip-arrow-top', '0%')
          popup.style.setProperty(
            '--v-tooltip-arrow-translate',
            'translate(-50%, calc(-100% + var(--v-tooltip-arrow-overlap)))'
          )
          popup.style.setProperty(
            '--v-tooltip-arrow-border-color',
            'transparent transparent var(--v-tooltip-background-color) transparent'
          )
          break
        case 'left':
          popup.style.setProperty(
            '--v-tooltip-translate',
            'translate(calc(-100% - var(--v-tooltip-arrow-offset)), -50%)'
          )
          popup.style.setProperty('--v-tooltip-arrow-left', '100%')
          popup.style.setProperty('--v-tooltip-arrow-top', 'calc(50% + var(--v-tooltip-top-offset))')
          popup.style.setProperty(
            '--v-tooltip-arrow-translate',
            'translate(calc(-1 * var(--v-tooltip-arrow-overlap)), -50%)'
          )
          popup.style.setProperty(
            '--v-tooltip-arrow-border-color',
            'transparent transparent transparent var(--v-tooltip-background-color)'
          )
          break
        case 'right':
          popup.style.setProperty('--v-tooltip-translate', 'translate(var(--v-tooltip-arrow-offset), -50%)')
          popup.style.setProperty('--v-tooltip-arrow-left', '0%')
          popup.style.setProperty('--v-tooltip-arrow-top', 'calc(50% + var(--v-tooltip-top-offset))')
          popup.style.setProperty(
            '--v-tooltip-arrow-translate',
            'translate(calc(-100% + var(--v-tooltip-arrow-overlap)), -50%)'
          )
          popup.style.setProperty(
            '--v-tooltip-arrow-border-color',
            'transparent var(--v-tooltip-background-color) transparent  transparent'
          )
          break
        default:
          break
      }
    } else {
      popup.style.setProperty(`--v-tooltip-${key}`, val)
    }
  }
}

const positionPortalTooltip = (el, popup) => {
  const rect = el.getBoundingClientRect()
  const placement = el._vTooltipPortalValue?.theme?.placement
  const scrollLeft = window.scrollX || document.documentElement.scrollLeft
  const scrollTop = window.scrollY || document.documentElement.scrollTop

  switch (placement) {
    case 'bottom':
      popup.style.left = `${rect.left + scrollLeft + rect.width / 2}px`
      popup.style.top = `${rect.bottom + scrollTop}px`
      break
    case 'left':
      popup.style.left = `${rect.left + scrollLeft}px`
      popup.style.top = `${rect.top + scrollTop + rect.height / 2}px`
      break
    case 'right':
      popup.style.left = `${rect.right + scrollLeft}px`
      popup.style.top = `${rect.top + scrollTop + rect.height / 2}px`
      break
    case 'top':
    default:
      popup.style.left = `${rect.left + scrollLeft + rect.width / 2}px`
      popup.style.top = `${rect.top + scrollTop}px`
      break
  }
}

const ensurePortalTooltipPopup = (el) => {
  if (el._vTooltipPortalPopup) return el._vTooltipPortalPopup

  const popup = document.createElement('span')
  popup.className = `${TOOLTIP_POPUP_CLASS} ${TOOLTIP_PORTAL_CLASS}`
  popup.setAttribute('aria-hidden', 'true')
  document.body.appendChild(popup)
  el._vTooltipPortalPopup = popup
  return popup
}

const showPortalTooltip = (el) => {
  const popup = el._vTooltipPortalPopup
  if (!popup) return

  positionPortalTooltip(el, popup)
  popup.classList.add(TOOLTIP_PORTAL_VISIBLE_CLASS)
}

const updatePortalTooltipPosition = (el) => {
  if (!el._vTooltipPortalPopup?.classList.contains(TOOLTIP_PORTAL_VISIBLE_CLASS)) return
  positionPortalTooltip(el, el._vTooltipPortalPopup)
}

const hidePortalTooltip = (el) => {
  el._vTooltipPortalPopup?.classList.remove(TOOLTIP_PORTAL_VISIBLE_CLASS)
}

const syncPortalTooltipPopup = (el, value) => {
  if (!hasContent(value.text)) {
    removePortalTooltipPopup(el)
    return
  }

  const popup = ensurePortalTooltipPopup(el)
  renderTooltipContent(popup, value.text, value.title)
  applyPortalTheme(popup, value.theme)
  positionPortalTooltip(el, popup)
}

const setupPortalTooltip = (el, value) => {
  el._vTooltipPortalValue = value
  syncPortalTooltipPopup(el, value)
  if (el._vTooltipShowPortal) return

  el._vTooltipShowPortal = () => showPortalTooltip(el)
  el._vTooltipHidePortal = () => hidePortalTooltip(el)
  el._vTooltipUpdatePortalPosition = () => updatePortalTooltipPosition(el)
  el.addEventListener('mouseenter', el._vTooltipShowPortal)
  el.addEventListener('focusin', el._vTooltipShowPortal)
  el.addEventListener('mouseleave', el._vTooltipHidePortal)
  el.addEventListener('focusout', el._vTooltipHidePortal)
  window.addEventListener('scroll', el._vTooltipUpdatePortalPosition, true)
  window.addEventListener('resize', el._vTooltipUpdatePortalPosition)
}

const cleanupPortalTooltip = (el) => {
  if (el._vTooltipShowPortal) {
    el.removeEventListener('mouseenter', el._vTooltipShowPortal)
    el.removeEventListener('focusin', el._vTooltipShowPortal)
  }
  if (el._vTooltipHidePortal) {
    el.removeEventListener('mouseleave', el._vTooltipHidePortal)
    el.removeEventListener('focusout', el._vTooltipHidePortal)
  }
  if (el._vTooltipUpdatePortalPosition) {
    window.removeEventListener('scroll', el._vTooltipUpdatePortalPosition, true)
    window.removeEventListener('resize', el._vTooltipUpdatePortalPosition)
  }
  el._vTooltipShowPortal = null
  el._vTooltipHidePortal = null
  el._vTooltipUpdatePortalPosition = null
  el._vTooltipPortalValue = null
  removePortalTooltipPopup(el)
}

export default {
  updateTooltip(el, { value, modifiers }) {
    if (typeof value === 'string') {
      // we can pass either a string
      cleanupPortalTooltip(el)
      if (hasContent(value)) {
        el.setAttribute('data-v-tooltip', value)
      } else {
        el.removeAttribute('data-v-tooltip')
        el.classList.remove('data-v-tooltip')
      }

      // this check if when v-tooltip receives string with .arrow modifier
      if (modifiers.arrow) {
        el.style.setProperty('--v-tooltip-arrow-display', 'inline')
      }

      syncTooltipPopup(el, value)
    } else if (value && typeof value === 'object') {
      // or an object
      if (hasContent(value.text)) {
        el.setAttribute('data-v-tooltip', value.text)
      } else {
        el.removeAttribute('data-v-tooltip')
        el.classList.remove('data-v-tooltip')
      }
      removeTooltipPopup(el)

      if (value.portal) {
        setupPortalTooltip(el, value)
        return
      }

      cleanupPortalTooltip(el)

      if (value.displayArrow || modifiers.arrow) {
        // if there is a prop global: true then mutate the :root css variables
        // otherwise it adds given variables to the element, which makes it possible to be different than others
        const targetEl = value.global ? document.documentElement : el
        targetEl.style.setProperty('--v-tooltip-arrow-display', 'inline')
      }
      if (value.theme) {
        // if there is a prop global: true then mutate the :root css variables
        // otherwise it adds given variables to the element, which makes it possible to be different than others
        const targetEl = value.global ? document.documentElement : el
        for (const [key, val] of Object.entries(value.theme)) {
          if (key === 'placement') {
            switch (val) {
              case 'top':
                targetEl.style.setProperty('--v-tooltip-left', '50%')
                targetEl.style.setProperty('--v-tooltip-top', '0%')
                targetEl.style.setProperty(
                  '--v-tooltip-translate',
                  'translate(-50%, calc(-100% - var(--v-tooltip-arrow-offset)))'
                )
                targetEl.style.setProperty('--v-tooltip-arrow-left', 'calc(50% + var(--v-tooltip-left-offset))')
                targetEl.style.setProperty('--v-tooltip-arrow-top', '100%')
                targetEl.style.setProperty(
                  '--v-tooltip-arrow-translate',
                  'translate(-50%, calc(-1 * var(--v-tooltip-arrow-overlap)))'
                )
                targetEl.style.setProperty(
                  '--v-tooltip-arrow-border-color',
                  'var(--v-tooltip-background-color) transparent transparent transparent'
                )
                break
              case 'bottom':
                targetEl.style.setProperty('--v-tooltip-left', '50%')
                targetEl.style.setProperty('--v-tooltip-top', '100%')
                targetEl.style.setProperty('--v-tooltip-translate', 'translate(-50%, var(--v-tooltip-arrow-offset))')
                targetEl.style.setProperty('--v-tooltip-arrow-left', 'calc(50% + var(--v-tooltip-left-offset))')
                targetEl.style.setProperty('--v-tooltip-arrow-top', '0%')
                targetEl.style.setProperty(
                  '--v-tooltip-arrow-translate',
                  'translate(-50%, calc(-100% + var(--v-tooltip-arrow-overlap)))'
                )
                targetEl.style.setProperty(
                  '--v-tooltip-arrow-border-color',
                  'transparent transparent var(--v-tooltip-background-color) transparent'
                )
                break
              case 'left':
                targetEl.style.setProperty('--v-tooltip-left', '0%')
                targetEl.style.setProperty('--v-tooltip-top', '50%')
                targetEl.style.setProperty(
                  '--v-tooltip-translate',
                  'translate(calc(-100% - var(--v-tooltip-arrow-offset)), -50%)'
                )
                targetEl.style.setProperty('--v-tooltip-arrow-left', '100%')
                targetEl.style.setProperty('--v-tooltip-arrow-top', 'calc(50% + var(--v-tooltip-top-offset))')
                targetEl.style.setProperty(
                  '--v-tooltip-arrow-translate',
                  'translate(calc(-1 * var(--v-tooltip-arrow-overlap)), -50%)'
                )
                targetEl.style.setProperty(
                  '--v-tooltip-arrow-border-color',
                  'transparent transparent transparent var(--v-tooltip-background-color)'
                )
                break
              case 'right':
                targetEl.style.setProperty('--v-tooltip-left', '100%')
                targetEl.style.setProperty('--v-tooltip-top', '50%')
                targetEl.style.setProperty('--v-tooltip-translate', 'translate(var(--v-tooltip-arrow-offset), -50%)')
                targetEl.style.setProperty('--v-tooltip-arrow-left', '0%')
                targetEl.style.setProperty('--v-tooltip-arrow-top', 'calc(50% + var(--v-tooltip-top-offset))')
                targetEl.style.setProperty(
                  '--v-tooltip-arrow-translate',
                  'translate(calc(-100% + var(--v-tooltip-arrow-overlap)), -50%)'
                )
                targetEl.style.setProperty(
                  '--v-tooltip-arrow-border-color',
                  'transparent var(--v-tooltip-background-color) transparent  transparent'
                )
                break
              default:
                break
            }
          } else if (key === 'offset' && !value.global) {
            for (const direction of val) {
              if (direction === 'left') {
                targetEl.style.setProperty(
                  '--v-tooltip-left-offset',
                  `-${targetEl.scrollWidth - targetEl.clientWidth}px`
                )
              } else if (direction === 'right') {
                targetEl.style.setProperty(
                  '--v-tooltip-left-offset',
                  `${targetEl.scrollWidth - targetEl.clientWidth}px`
                )
              } else if (direction === 'top') {
                targetEl.style.setProperty(
                  '--v-tooltip-top-offset',
                  `-${targetEl.scrollHeight - targetEl.clientHeight}px`
                )
              } else if (direction === 'bottom') {
                targetEl.style.setProperty(
                  '--v-tooltip-top-offset',
                  `${targetEl.scrollHeight - targetEl.clientHeight}px`
                )
              }
            }
          } else {
            targetEl.style.setProperty(`--v-tooltip-${key}`, val)
          }
        }
      }

      if (!value.global) {
        syncTooltipPopup(el, value.text, value.title)
      }
    } else {
      el.removeAttribute('data-v-tooltip')
      el.classList.remove('data-v-tooltip')
      removeTooltipPopup(el)
      cleanupPortalTooltip(el)
    }
  },
  // hooks
  mounted(el, { value, dir, modifiers }) {
    // v-tooltips with global prop won't show the tooltip
    // also object notation without text prop won't show neither
    if (value && typeof value === 'object' && !value.global && hasContent(value.text)) {
      el.classList.add('data-v-tooltip')
    } else if (typeof value === 'string' && hasContent(value)) {
      el.classList.add('data-v-tooltip')
    }

    // to use functions in Vue's directives which are inside this object, we can't use this, we have to use dir, which is the directive object
    dir.updateTooltip(el, { value, modifiers })
  },
  updated(el, { value, dir, modifiers }) {
    dir.updateTooltip(el, { value, modifiers })

    // Toggle class based on whether tooltip has content (handles dynamic text changes)
    const shouldShowTooltip =
      typeof value === 'string'
        ? hasContent(value)
        : !!value && typeof value === 'object' && !value.global && hasContent(value.text)

    if (shouldShowTooltip) {
      el.classList.add('data-v-tooltip')
    } else {
      el.classList.remove('data-v-tooltip')
    }
  },
  unmounted(el) {
    removeTooltipPopup(el)
    cleanupPortalTooltip(el)
  },
}
